'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { DiscoveryPageLayout } from '@/domains/paydirt/layout';
import { DraftClaimCard } from '@/domains/paydirt/layout/DraftClaimCard';
import { HaulReportsFeed } from '@/domains/paydirt/layout/HaulReportsFeed';
import { paydirtConfig } from '@/config/paydirt';
import { useSiteAppearance } from '@/platform/siteappearance/SiteAppearanceProvider';
import { listClaims, type ClaimSummary } from '@/domains/paydirt/claims/api';
import { listForumPosts, type ForumPost } from '@/platform/forum/api';
import { useAuth } from '@/platform/auth/useAuth';
import { OpsHomeRedirect } from '@/platform/servicedesk/OpsHomeRedirect';

const { forum } = paydirtConfig;
const opsEnabled = process.env.NEXT_PUBLIC_COMMUNIO_OPS === 'true';

export default function HomePage() {
  if (opsEnabled) {
    return <OpsHomeRedirect />;
  }
  return <PaydirtHomePage />;
}

function PaydirtHomePage() {
  const { appearance } = useSiteAppearance();
  const { isAuthenticated, sessionReady, profile } = useAuth();
  const [drafts, setDrafts] = useState<ClaimSummary[]>([]);
  const [posts, setPosts] = useState<ForumPost[]>([]);

  useEffect(() => {
    listForumPosts({ sort: 'hot', subSlug: forum.defaultSubSlug })
      .then((p) => setPosts(p.slice(0, 5)))
      .catch(() => setPosts([]));
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !sessionReady || !profile) {
      setDrafts([]);
      return;
    }
    listClaims({ status: 'draft', limit: 10 })
      .then((d) => setDrafts(d.claims ?? []))
      .catch(() => setDrafts([]));
  }, [isAuthenticated, sessionReady, profile]);

  const rightRail =
    posts.length > 0 ? (
      <div className="rail-card">
        <h4>Trending in {forum.label.toLowerCase()}</h4>
        {posts.map((p) => (
          <Link key={p.id} href={`/forum/posts/${p.id}/`} className="rail-card__link">
            {p.title}
          </Link>
        ))}
      </div>
    ) : undefined;

  const backdropUrl = appearance.backgroundImageUrl || paydirtConfig.assets.mainPageBackdrop;
  const showHomeBackdrop = appearance.backgroundScope !== 'site' || !appearance.backgroundImageUrl;

  return (
    <div
      className={showHomeBackdrop ? 'paydirt-main-backdrop' : undefined}
      style={
        showHomeBackdrop
          ? {
              ['--paydirt-page-backdrop' as string]: `url('${backdropUrl}')`,
            }
          : undefined
      }
    >
      <DiscoveryPageLayout
        title={paydirtConfig.brandName}
        description={paydirtConfig.tagline}
        right={rightRail}
      >
        {drafts.length > 0 && (
          <section className="social-feed" aria-label="Draft haul reports">
            <h2>Your drafts</h2>
            {drafts.map((d) => (
              <DraftClaimCard key={d.id} claim={d} />
            ))}
          </section>
        )}
        <HaulReportsFeed />
        <p className="paydirt-main-backdrop__credit">Background photo: Stockcake (gold panning)</p>
      </DiscoveryPageLayout>
    </div>
  );
}
